"use client";

import clsx from "clsx";
import { useTranslations } from "next-intl";
import { useIsLens } from "@/lib/audienceLens/AudienceLensContext";

/**
 * M3 — Enterprise lens evidence panel. Surfaces what an enterprise
 * buyer's procurement / integration / IT-architecture review will ask:
 * integration surface (API endpoints, SDK languages), multi-tenant
 * boundaries, data residency, SLAs (uptime, P99, RTO/RPO). Renders
 * ONLY on `?lens=enterprise`.
 *
 * Variants:
 *
 *   variant="contract-captured" — anchored to ContractCapture-with-
 *                                  handshake (2.1 / 4.5 / 5.5). Shows
 *                                  the integration shape + boundaries.
 */
export function EnterpriseEvidence({
  variant,
  className,
}: {
  variant: "contract-captured";
  className?: string;
}) {
  const t = useTranslations("enterpriseEvidence");
  const isEnterprise = useIsLens("enterprise");
  if (!isEnterprise) return null;

  if (variant === "contract-captured") {
    return (
      <div className={clsx("glass p-4", className)}>
        <div className="flex items-baseline justify-between">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-verified">
            {t("kicker")}
          </p>
          <span className="rounded-full border border-state-verified/30 bg-state-verified/8 px-2 py-0.5 font-mono text-[8px] uppercase tracking-wider text-state-verified">
            {t("lensChip")}
          </span>
        </div>

        <div className="mt-3 grid grid-cols-3 gap-3 text-[11px]">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
              {t("integrationTitle")}
            </p>
            <ul className="mt-2 flex flex-col gap-1 text-orbitra-paper">
              <li>{t("integrationRest")} <span className="font-mono text-state-verified">/v1/contracts</span></li>
              <li>{t("integrationSse")} <span className="font-mono text-state-verified">/sse/&lt;perspective&gt;</span></li>
              <li>{t("integrationWebhook")} <span className="font-mono text-state-verified">events.*</span></li>
              <li>{t("integrationSdk")} <span className="font-mono">{t("integrationSdkValue")}</span></li>
              <li>{t("integrationAuth")} <span className="font-mono">{t("integrationAuthValue")}</span></li>
            </ul>
          </div>
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
              {t("tenantTitle")}
            </p>
            <ul className="mt-2 flex flex-col gap-1 text-orbitra-paper">
              <li>{t("tenantScoping")} <span className="font-mono">{t("tenantScopingValue")}</span></li>
              <li>{t("tenantEncryption")} <span className="font-mono">{t("tenantEncryptionValue")}</span></li>
              <li>{t("tenantIsolation")} <span className="font-mono">{t("tenantIsolationValue")}</span></li>
              <li>{t("tenantJoins")} <span className="font-mono text-policy-fail">{t("tenantJoinsValue")}</span></li>
              <li>{t("tenantDr")} <span className="font-mono">{t("tenantDrValue")}</span></li>
            </ul>
          </div>
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
              {t("slaTitle")}
            </p>
            <ul className="mt-2 flex flex-col gap-1 text-orbitra-paper">
              <li>{t("slaUptime")} <span className="font-mono text-state-verified">{t("slaUptimeValue")}</span> {t("slaUptimeNote")}</li>
              <li>{t("slaApiP99")} <span className="font-mono text-state-verified">{t("slaApiP99Value")}</span></li>
              <li>{t("slaEventDelivery")} <span className="font-mono text-state-verified">{t("slaEventDeliveryValue")}</span></li>
              <li>{t("slaRpo")} <span className="font-mono">{t("slaRpoValue")}</span></li>
              <li>{t("slaRto")} <span className="font-mono">{t("slaRtoValue")}</span></li>
            </ul>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-3 text-[10px]">
          <div className="rounded border border-state-verified/30 bg-state-verified/8 px-3 py-2">
            <p className="font-mono text-[8px] uppercase tracking-[0.18em] text-state-verified">{t("residencyTitle")}</p>
            <ul className="mt-1 text-orbitra-paper">
              <li>{t("residencyEu")} <span className="font-mono">{t("residencyEuValue")}</span></li>
              <li>{t("residencyUae")} <span className="font-mono">{t("residencyUaeValue")}</span></li>
              <li>{t("residencyBr")} <span className="font-mono">{t("residencyBrValue")}</span></li>
              <li>{t("residencyCrossBorder")} <span className="font-mono">{t("residencyCrossBorderValue")}</span></li>
            </ul>
          </div>
          <div className="rounded border border-state-verified/30 bg-state-verified/8 px-3 py-2">
            <p className="font-mono text-[8px] uppercase tracking-[0.18em] text-state-verified">{t("procurementTitle")}</p>
            <ul className="mt-1 text-orbitra-paper">
              <li>{t("procurementSoc2")} <span className="font-mono">{t("procurementSoc2Value")}</span></li>
              <li>{t("procurementIso")} <span className="font-mono">{t("procurementIsoValue")}</span></li>
              <li>{t("procurementVendor")} <span className="font-mono">{t("procurementVendorValue")}</span></li>
              <li>{t("procurementContract")} <span className="font-mono">{t("procurementContractValue")}</span></li>
            </ul>
          </div>
        </div>

        <p className="mt-3 font-mono text-[9px] text-orbitra-muted">
          {t("footerPrefix")}
          <span className="ms-1 font-mono text-state-verified">{t("footerToggle")}</span>{t("footerToggleSuffix")}
        </p>
      </div>
    );
  }
  return null;
}
